import React, { useState, useEffect, useCallback } from 'react';
import { getPortfolio, deletePortfolio, updatePortfolio, PortfolioItem } from '../services/portfolio';
import AnimatedSection from './AnimatedSection';
import PortfolioUpload from './PortfolioUpload';

const PortfolioManagement: React.FC = () => {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    type: '' as 'image' | 'video' | '',
    featured: undefined as boolean | undefined,
  });

  const loadPortfolio = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPortfolio({
        category: filters.category || undefined,
        type: filters.type || undefined,
        featured: filters.featured,
        limit: 100,
      });
      setPortfolioItems(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio items');
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.type, filters.featured]);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) {
      return;
    }

    try {
      await deletePortfolio(id);
      setPortfolioItems(items => items.filter(item => item.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete portfolio item');
    }
  };

  const handleEdit = (item: PortfolioItem) => {
    setEditingItem(item);
    setShowUpload(false);
  };

  const handleUpdate = async (formData: FormData | Partial<PortfolioItem>) => {
    if (!editingItem) return;

    try {
      const response = await updatePortfolio(editingItem.id, formData);
      setPortfolioItems(items =>
        items.map(item => (item.id === editingItem.id ? response.data : item))
      );
      setEditingItem(null);
      // Refresh the list to ensure we have the latest data
      await loadPortfolio();
      alert('Portfolio item updated successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update portfolio item');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  return (
    <section className="py-20 brand-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-dark-900 dark:text-neutral-50 mb-2">
                  Portfolio <span className="text-primary-500">Management</span>
                </h1>
                <p className="text-xl text-dark-600 dark:text-neutral-300">
                  Manage your portfolio items
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUpload(!showUpload);
                  setEditingItem(null);
                }}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
              >
                {showUpload ? 'Hide Upload' : '+ Upload New Item'}
              </button>
            </div>
          </div>
        </AnimatedSection>

        {/* Upload Section */}
        {showUpload && !editingItem && (
          <div className="mb-8">
            <PortfolioUpload onUploadSuccess={loadPortfolio} />
          </div>
        )}

        {/* Edit Form */}
        {editingItem && (
          <AnimatedSection animation="fade-in">
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-dark-900 dark:text-neutral-50">
                  Edit Portfolio Item
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-dark-600 dark:text-neutral-400 hover:text-dark-900 dark:hover:text-neutral-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <EditPortfolioForm item={editingItem} onUpdate={handleUpdate} onCancel={handleCancelEdit} />
            </div>
          </AnimatedSection>
        )}

        {/* Filters */}
        <AnimatedSection animation="fade-in" delay={100}>
          <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
            <h3 className="text-lg font-semibold text-dark-900 dark:text-neutral-50 mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  placeholder="Filter by category"
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value as 'image' | 'video' | '' })}
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                >
                  <option value="">All Types</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  Featured
                </label>
                <select
                  value={filters.featured === undefined ? '' : filters.featured.toString()}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      featured: e.target.value === '' ? undefined : e.target.value === 'true',
                    })
                  }
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                >
                  <option value="">All</option>
                  <option value="true">Featured Only</option>
                  <option value="false">Not Featured</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ category: '', type: '', featured: undefined })}
                  className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Portfolio Items List */}
        <AnimatedSection animation="fade-in" delay={200}>
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-dark-600 dark:text-neutral-300">Loading portfolio items...</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg p-4 text-center">
              {error}
            </div>
          ) : portfolioItems.length === 0 ? (
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-12 border border-primary-500/20 text-center">
              <p className="text-dark-600 dark:text-neutral-300 text-lg">No portfolio items found.</p>
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50">
                  Portfolio Items ({portfolioItems.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-primary-500/20">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Preview</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Title</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Category</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Featured</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioItems.map((item) => (
                      <tr key={item.id} className="border-b border-primary-500/10 hover:bg-primary-500/5 transition-colors">
                        <td className="py-3 px-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden">
                            {item.type === 'video' ? (
                              <video
                                src={item.media}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                              />
                            ) : (
                              <img
                                src={item.media}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = 'https://via.placeholder.com/64';
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-semibold text-dark-900 dark:text-neutral-50 text-sm">{item.title}</p>
                            <p className="text-xs text-dark-600 dark:text-neutral-400 line-clamp-1">{item.description}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.type === 'video'
                              ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                              : 'bg-green-500/20 text-green-600 dark:text-green-400'
                          }`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-dark-600 dark:text-neutral-300">{item.category}</span>
                        </td>
                        <td className="py-3 px-4">
                          {item.featured ? (
                            <span className="px-2 py-1 bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded text-xs font-medium">
                              Yes
                            </span>
                          ) : (
                            <span className="text-sm text-dark-400 dark:text-neutral-500">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-primary-500 hover:bg-primary-500/10 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Edit Form Component
interface EditPortfolioFormProps {
  item: PortfolioItem;
  onUpdate: (data: FormData | Partial<PortfolioItem>) => void;
  onCancel: () => void;
}

const EditPortfolioForm: React.FC<EditPortfolioFormProps> = ({ item, onUpdate, onCancel }) => {
  const [formData, setFormData] = useState({
    title: item.title,
    description: item.description,
    category: item.category,
    websiteUrl: item.websiteUrl,
    featured: item.featured || false,
  });
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mediaFile) {
        // If new media file, use FormData
        const formDataToSend = new FormData();
        formDataToSend.append('media', mediaFile);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('websiteUrl', formData.websiteUrl);
        formDataToSend.append('featured', formData.featured.toString());
        onUpdate(formDataToSend);
      } else {
        // Otherwise, use JSON
        onUpdate(formData);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update portfolio item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Media File (Image/Video) - Leave empty to keep current
        </label>
        <input
          type="file"
          accept="image/*,video/*"
          onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
        <p className="text-xs text-dark-500 dark:text-neutral-400 mt-1">
          Current: {item.type} - {item.media.substring(0, 50)}...
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Title
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
            Category
          </label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
            Website URL
          </label>
          <input
            type="url"
            name="websiteUrl"
            value={formData.websiteUrl}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="edit-featured"
          name="featured"
          checked={formData.featured}
          onChange={handleInputChange}
          className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
        />
        <label htmlFor="edit-featured" className="ml-2 text-sm text-dark-600 dark:text-neutral-300">
          Featured Item
        </label>
      </div>

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-3 px-6 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          {loading ? 'Updating...' : 'Update Portfolio Item'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-dark-600 hover:bg-dark-700 text-white font-medium rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PortfolioManagement;

